import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DataDashboard from '../../../common/DataDashboard';
import { AdminUser } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';

interface AgenticDashboardProps {
  user: AdminUser;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

interface AgenticLogEntry {
  _id: string;
  id?: string;
  job_id?: string;
  timestamp?: string;
  event_type?: string;
  agent?: string;
  product_id?: string;
  product_name?: string;
  url?: string;
  message?: string;
  status?: string;
  region?: string;
  user_id?: string;
  details?: any;
}

interface PaginationState {
  page: number;
  limit: number;
  total: number;
  pages: number;
  has_next: boolean;
  has_prev: boolean;
}

const defaultPagination: PaginationState = {
  page: 1,
  limit: 50,
  total: 0,
  pages: 0,
  has_next: false,
  has_prev: false
};

const AgenticDashboard: React.FC<AgenticDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token } = useAuth();
  const [logs, setLogs] = useState<AgenticLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>(defaultPagination);

  const fetchLogs = useCallback(async (
    page: number = 1,
    limit: number = defaultPagination.limit,
    columnFilters: Record<string, string> = {}
  ) => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const skip = (page - 1) * limit;
      const params = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString()
      });

      // Apply column filters
      console.log('🔍 AgenticDashboard - fetchLogs received columnFilters:', columnFilters);
      if (columnFilters.job_id && columnFilters.job_id.trim()) {
        params.append('job_id', columnFilters.job_id.trim());
      }
      if (columnFilters.product_id && columnFilters.product_id.trim()) {
        params.append('product_id', columnFilters.product_id.trim());
      }
      if (columnFilters.event_type && columnFilters.event_type.trim()) {
        params.append('event_type', columnFilters.event_type.trim());
      }
      if (columnFilters.agent && columnFilters.agent.trim()) {
        params.append('agent', columnFilters.agent.trim());
      }
      if (columnFilters.region && columnFilters.region.trim()) {
        params.append('region', columnFilters.region.trim());
      }
      console.log('🔍 AgenticDashboard - Final query params:', params.toString());

      const response = await fetch(`${API_ENDPOINTS.AGENTIC_LOGS}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load logs (${response.status})`);
      }

      const result = await response.json();
      if (!result.success) {
        throw new Error(result.message || 'Failed to load logs');
      }

      const data: AgenticLogEntry[] = (result.data || []).map((item: AgenticLogEntry) => ({
        ...item,
        id: item._id || item.id
      }));

      setLogs(data);

      const total = result.pagination?.total || 0;
      const computedLimit = result.pagination?.limit || limit;
      const computedSkip = result.pagination?.skip || skip;
      const pageNumber = Math.floor(computedSkip / computedLimit) + 1;
      const totalPages = computedLimit > 0 ? Math.ceil(total / computedLimit) : 0;

      setPagination({
        page: pageNumber,
        limit: computedLimit,
        total,
        pages: totalPages,
        has_next: computedSkip + computedLimit < total,
        has_prev: computedSkip > 0
      });
    } catch (err: any) {
      console.error('Error fetching agentic logs:', err);
      setError(err.message || 'Failed to load logs');
      setLogs([]);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLogs(1, pagination.limit, {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const handleDataFetch = useCallback((params: {
    page: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    columnFilters?: Record<string, string>;
  }) => {
    console.log('🔍 AgenticDashboard - handleDataFetch called with:', params);
    const nextLimit = params.limit || defaultPagination.limit;
    const filters = params.columnFilters || {};
    console.log('🔍 AgenticDashboard - Calling fetchLogs with filters:', filters);
    fetchLogs(params.page, nextLimit, filters);
  }, [fetchLogs]);

  const columns = useMemo(() => ([
    {
      key: 'timestamp',
      title: 'Timestamp',
      type: 'text' as const,
      sortable: false,
      editable: false,
      render: (value: string) => {
        if (!value) return '-';
        try {
          const date = new Date(value);
          return new Intl.DateTimeFormat(undefined, {
            dateStyle: 'medium',
            timeStyle: 'medium'
          }).format(date);
        } catch {
          return value;
        }
      }
    },
    {
      key: 'event_type',
      title: 'Event Type',
      type: 'text' as const,
      sortable: false,
      editable: false,
      filterable: true
    },
    {
      key: 'agent',
      title: 'Agent',
      type: 'text' as const,
      sortable: false,
      editable: false,
      filterable: true
    },
    {
      key: 'product_id',
      title: 'Product ID',
      type: 'text' as const,
      sortable: false,
      editable: false,
      filterable: true
    },
    {
      key: 'product_name',
      title: 'Product',
      type: 'text' as const,
      sortable: false,
      editable: false,
      filterable: true
    },
    {
      key: 'url',
      title: 'URL',
      type: 'text' as const,
      sortable: false,
      editable: false,
      render: (value: string) => {
        if (!value) return '-';
        return (
          <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>
            {value}
          </a>
        );
      }
    },
    {
      key: 'message',
      title: 'Message',
      type: 'text' as const,
      sortable: false,
      editable: false
    },
    {
      key: 'status',
      title: 'Status',
      type: 'text' as const,
      sortable: false,
      editable: false
    },
    {
      key: 'region',
      title: 'Region',
      type: 'text' as const,
      sortable: false,
      editable: false,
      filterable: true
    },
    {
      key: 'job_id',
      title: 'Job ID',
      type: 'text' as const,
      sortable: false,
      editable: false,
      filterable: true
    },
    {
      key: 'details',
      title: 'Details',
      type: 'text' as const,
      sortable: false,
      editable: false,
      render: (value: any) => {
        if (!value || Object.keys(value).length === 0) return '-';
        try {
          return <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>;
        } catch {
          return String(value);
        }
      }
    }
  ]), []);

  const transformedLogs = useMemo(() => (
    logs.map(log => ({
      ...log,
      id: log.id || log._id
    }))
  ), [logs]);

  return (
    <div className="agentic-dashboard">
      <DataDashboard
        title="Agentic Logs"
        collection="ai_logs"
        data={transformedLogs}
        columns={columns}
        isLoading={isLoading}
        error={error}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataFetch={handleDataFetch}
        onDataChange={setLogs}
        showAddButton={false}
        showSearch={true}
        showSelection={false}
        showEditButton={false}
        showDeleteButton={false}
        layoutType="admin"
        paginationInfo={pagination}
      />
    </div>
  );
};

export default AgenticDashboard;
